import { computed, ref, useContext, useRoute } from '@nuxtjs/composition-api'
import { LocaleObject } from '@nuxtjs/i18n'

const changedRoute = ref<string | null>(null)
const urlLocales = ref<Record<string, string> | null>(null)
const changeLinksWithQuery = ref(true)
const changeLinksWithHash = ref(true)

export const useLocale = () => {
  const route = useRoute()
  const { i18n, switchLocalePath, localePath, getRouteBaseName } = useContext()

  const currentCode = computed(() => i18n.locale)
  const i18nLocaleCodes = computed(() =>
    (i18n.locales as LocaleObject[])?.map((locale) => locale.code)
  )

  const resetState = () => {
    changedRoute.value = null
    urlLocales.value = null
  }

  const doChangeLocaleLinks = (
    articleUrlLocales?: Record<string, string>,
    withQuery = true,
    withHash = true
  ) => {
    if (articleUrlLocales) {
      changedRoute.value = route.value.name as string
      urlLocales.value = articleUrlLocales
    } else {
      resetState()
    }
    changeLinksWithQuery.value = withQuery
    changeLinksWithHash.value = withHash
  }

  const localeLinks = computed(() => {
    if (changedRoute.value === route.value.name && urlLocales.value) {
      return i18nLocaleCodes.value
        .map((locale) => ({
          key: locale,
          text: locale,
          url: localePath(
            {
              name: getRouteBaseName(),
              params: route.value.params.pathMatch
                ? { pathMatch: urlLocales.value![locale] }
                : { slug: urlLocales.value![locale] },
              query: changeLinksWithQuery.value ? route.value.query : undefined,
              hash: changeLinksWithHash.value ? route.value.hash : undefined,
            },
            locale
          ),
        }))
        .filter((link) => link.url)
    }

    return i18nLocaleCodes.value.map((locale) => ({
      key: locale,
      text: locale,
      url:
        changeLinksWithQuery.value && changeLinksWithHash.value
          ? switchLocalePath(locale)
          : localePath(
              {
                name: getRouteBaseName(),
                params: route.value.params,
                query: changeLinksWithQuery.value
                  ? route.value.query
                  : undefined,
                hash: changeLinksWithHash.value ? route.value.hash : undefined,
              },
              locale
            ),
    }))
  })

  return {
    currentCode,
    localeLinks,

    doChangeLocaleLinks,
  }
}
